// INVARIANT(scriptera): СЦЕНА-ПРИМЕР НАРИСОВАНА В ДВУХ МЕСТАХ — веб (apps/web: components/auth/app-onboarding.tsx,
// components/auth/onboarding.css, components/mock-phone.{tsx,css}, app/[locale]/wizard-scene/[scene]/*,
// lib/scene-script.ts) и iOS (scriptera-app: ios/App/App/Kit/Scenes/*). Правишь вид, шаг, текст-раскладку, тайминг,
// цвет или размер — правь ОБА места тем же днём и сверяй кадры склейкой «веб | iOS» (~/ops/native-scenes). Причина:
// владелец 26.10.2026 — сцены в приложении нативные, «пиксель в пиксель». Канон: ~/projects/NATIVE-SCENES-TZ.md,
// docs/HOW-IT-WORKS.md §«Сцены-примеры».

// ПРИМЕР НА ДАННЫХ КЛИЕНТА (бэклог «Портрет клиента 2026-08-31»).
//
// Что меняется в сцене: кодовое слово и обещание — по нише человека; аватарка, имя, ник
// и картинка публикации — его собственные; имена в комментариях — его аудитории.
// «Рыба» текста при этом ОБЩАЯ (владелец 31.08: «не упарываться») — шаблоны фраз лежат
// в словаре (`auth.onboarding.pers.*`), сюда приезжают уже собранными.
//
// INVARIANT(scriptera): сама сцена (`Scene`, `useScenes`) ради подмены НЕ правится —
// строится её КОПИЯ снаружи, тем же механизмом, что у мастеров (lib/wizard-scene-texts.ts,
// applySceneTexts). Причина: сцену делят лендинг, тур и мастер, и выглядеть она обязана
// у всех одинаково. Канон: docs/NATIVE-WIZARD-PLAN.md ТЗ-7 Ш3.
import { expandGenderDeep, type PortraitGender } from '@scriptera/db/gender-word';
import type { SceneDef } from '@/lib/scene-script';
import { nichePerson, nichePostImg, nicheSalePostImg } from '@/lib/scene-assets';
import { applySceneTexts, isWizardScene, type SceneTexts } from '@/lib/wizard-scene-texts';

/** Ниша в языке показа: то, что отдаёт справочник (GET /api/niches). */
export interface NicheView {
  key: string;
  title: string;
  /** Кодовое слово примера: «ТРЕНИРОВКА». */
  word: string;
  /** Что бот обещает прислать: «план тренировок на неделю». */
  promise: string;
  /** Имя файла-подарка: «План тренировок.pdf». */
  gift: string;
}

/** Лицо клиента в примере: то, что собрал портрет по его каналу. */
export interface PersonalProfile {
  name?: string | null;
  nick?: string | null;
  /** Ссылка на НАШУ ручку аватарки подключения (наружу адрес площадки не уходит). */
  avatar?: string | null;
  /** Подпись его последней публикации. В сцену НЕ ставится: там пост зовёт написать
   *  кодовое слово, а настоящая подпись этого не говорит — картинку берём, текст нет. */
  post?: string | null;
  postImg?: string | null;
  /** Лица аудитории: имя и (О5.7) публичный адрес сохранённой аватарки комментатора
   *  (`/api/scene-face/<portraitId>/<idx>`); фото нет — только имя, в сцене кружок. */
  faces?: { name?: string | null; avatar?: string | null }[];
  /** Пол владельца аккаунта (О5.4): им раскрываются метки `{{g:жен|муж|нейтр}}` в
   *  репликах, которые бот говорит ОТ ЛИЦА человека («отправила в директ»). Пола нет —
   *  нейтральная форма. */
  gender?: PortraitGender | null;
  /** QUICK-START §10.2 п.2, ПРАВИЛО ЛИЦА (владелец 29.10.2026: «Лицо человека берётся из Инстаграма, если в Телеграме нет
   *  аватарки. Если в Телеграме есть аватарка — ставится она»): Телеграм-бот человека — имя в шапке Телеграма и аватарка
   *  (нет у бота — аватарка Инстаграма; нет обеих — пусто, фирменный кружок). Нет поля — Телеграм-часть сцены рисуется
   *  лицом и именем самого профиля. Собирает `withSceneFaces`. */
  tg?: { name?: string | null; avatar?: string | null } | null;
}

/** Подключённый аккаунт — то, что нужно правилу лица (структурно = `NicheAccount`). */
export interface SceneFaceAccount {
  channel: 'instagram' | 'telegram';
  name: string | null;
  username: string | null;
  avatarUrl: string | null;
}

/**
 * ПРАВИЛО ЛИЦА СЦЕН (QUICK-START §10.2 п.2, владелец 29.10.2026) — одно на мастер сайта, мастер приложения и «Быструю
 * настройку»: сцена Инстаграма — аватарка Инстаграм-аккаунта; сцена Телеграма — аватарка Телеграм-бота, нет — аватарка
 * Инстаграма; имя в шапке Телеграма — имя бота; нет ни одной аватарки — фирменный кружок (пустая строка), не чужое лицо.
 * `picked` — аккаунт анкеты (его лицо в `profile`); `accounts` — все подключённые (первый Инстаграм, первый бот).
 */
export function withSceneFaces(
  profile: PersonalProfile,
  picked: SceneFaceAccount | null,
  accounts: readonly SceneFaceAccount[] | null | undefined,
): PersonalProfile {
  const list = accounts ?? [];
  const ig = picked?.channel === 'instagram' ? picked : (list.find((a) => a.channel === 'instagram') ?? null);
  const bot = picked?.channel === 'telegram' ? picked : (list.find((a) => a.channel === 'telegram') ?? null);
  const igAva = ig?.avatarUrl || null;
  if (picked?.channel === 'telegram') {
    const ava = picked.avatarUrl || igAva;
    return { ...profile, avatar: ava, tg: { name: picked.name || picked.username || null, avatar: ava } };
  }
  if (!bot) return profile;
  return { ...profile, tg: { name: bot.name || bot.username || null, avatar: bot.avatarUrl || profile.avatar || null } };
}

/** Шаблоны фраз из словаря: `{promise}` и `{word}` подставляет вызывающий. */
export interface ScenePhrases {
  /** Первая реплика бота: «Привет! У меня для тебя {promise}. Жми кнопку ниже.» */
  reply: string;
  /** Выдача: «Готово, держи! {promise} по ссылке ниже». */
  give: string;
  /** Публичный ответ под комментарием. */
  pub?: string;
  /** Подпись поста: «Напиши {word} в комментарии — скину {promise}». */
  post?: string;
  /** Описание бота на заставке (s4): «Пришлю {promise} за подписку». */
  about?: string;
  /** Письма цепочки (s5, Д3): четыре сообщения по дням. Без ниши они говорили про план
   *  питания и минус 4 кг — под аватаркой юриста это враньё, поэтому текст общий, а
   *  что бот отдал — подставляется через `{promise}`. */
  msg1?: string;
  msg2?: string;
  msg3?: string;
  msg4?: string;
}

/** Длина обещания, после которой подпись поста режет его многоточием (= wizard-demo-url.ts / app-pages/wizard-demo.ts). */
export const POST_PROMISE_MAX = 44;

/** Первая буква — заглавная (подарок в начале фразы). */
export function capFirst(s: string): string {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

/**
 * Подарок для фраз §8.25 п.1 («Здравствуйте! {подарок} ждёт вас…» / «Hello! Your {gift} is ready…»): по-русски подарок
 * открывает фразу — с заглавной, в именительном; по-английски стоит после «Your» — без артикля справочника.
 */
export function giftLead(niche: Pick<NicheView, 'promise' | 'gift'>): string {
  // По-русски обещание ниши стоит в винительном («пришлю дорожную карту»), а фраза просит именительный — берётся название
  // подарка ниши (имя файла без расширения: «Дорожная карта»).
  if (/[а-яё]/i.test(niche.promise)) {
    const title = niche.gift.replace(/\.[a-z0-9]+$/i, '').trim();
    return capFirst(title || niche.promise);
  }
  return niche.promise.replace(/^(a|an|the)\s+/i, '');
}

/** Слова этой ниши в местах сцены. Чистая — юниты. */
export function nicheSceneTexts(niche: NicheView, phrases: ScenePhrases): SceneTexts {
  // РЕПЛИКА НАЧИНАЕТСЯ С ЗАГЛАВНОЙ (решение ведущего 26.10.2026 по ТЗ NATIVE-SCENES §16 В7(а)): обещание ниши в
  // справочнике — со строчной («пробный урок…»), и письмо, которое начинается с него, выходило «пробный урок… у тебя —
  // пользуйся!». Первая буква фразы — заглавная; слова внутри фразы не трогаем.
  const cap = (x: string) => (x ? x.charAt(0).toUpperCase() + x.slice(1) : x);
  const fill = (tpl: string) => cap(tpl.split('{promise}').join(niche.promise).split('{word}').join(niche.word));
  const out: SceneTexts = {
    comment: niche.word,
    reply: fill(phrases.reply),
    give: fill(phrases.give),
    file: niche.gift,
  };
  if (phrases.pub) out.pub = fill(phrases.pub);
  if (phrases.post) out.post = fill(phrases.post);
  if (phrases.about) out.about = fill(phrases.about);
  if (phrases.msg1) out.msg1 = fill(phrases.msg1);
  if (phrases.msg2) out.msg2 = fill(phrases.msg2);
  if (phrases.msg3) out.msg3 = fill(phrases.msg3);
  if (phrases.msg4) out.msg4 = fill(phrases.msg4);
  return out;
}

/**
 * ФУНКЦИЯ ПЕРЕВОДА для сборки фраз: подходят и клиентская (`useTranslations`), и
 * серверная (`getTranslations`) — обе зовутся ключом и значениями подстановки.
 */
export type ScenePhraseT = (key: string, values?: Record<string, string>) => string;

/**
 * ОДНА ТОЧКА ИСТИНЫ ДЛЯ СЛОВ НИШИ (Д3/Д4, владелец 03.09.2026). Раньше каждое из трёх
 * мест (мастер в вебе, страница сцены для приложения, тур) собирало фразы своим списком
 * ключей — стоило добавить письмо цепочки, и оно доезжало не всюду. Теперь список ключей
 * живёт здесь, а места зовут этот хелпер.
 *
 * `scene` — какая сцена стоит у анкеты: у «доводит до оплаты» (s3) сюжет НЕ про подарок
 * за подписку, а про выбор формата и оплату, поэтому у неё свои две фразы.
 */
export function nichePhrases(
  t: ScenePhraseT,
  niche: NicheView,
  gender?: PortraitGender | null,
  scene?: string | null,
): SceneTexts {
  if (scene === 's3') {
    // Ни кодового слова, ни подарка-файла: человек выбирает формат кнопкой и платит.
    // Имя файла и названия форматов — собственные тексты сцены (это товар КЛИЕНТА).
    // Заставка бота (`tgAbout.p`) — тоже без бренда сцены: у дефа s3 она теперь есть.
    return { reply: t('hello3'), give: t('done3'), about: t('about3') };
  }
  // СЦЕНЫ ЦЕЛИ g1 «Подписки» / g2 «Продажи» (ТЗ GOAL-SCENES-HELP, владелец 23.10.2026): сюжет тот
  // же, что у s2 (слово под постом → директ), но фразы формальные, на «вы», и у «Продаж» нет просьбы
  // подписаться — бот зовёт в Телеграм и дальше ведёт серией сообщений (msg1–msg3).
  if (scene === 'g1' || scene === 'g2') {
    const sale = scene === 'g2';
    return nicheSceneTexts(niche, {
      // §8.25 п.1: у «Продаж» подарок открывает фразу («Здравствуйте! {подарок} ждёт вас…») — с заглавной.
      reply: sale ? t('gReplySale', { promise: giftLead(niche) }) : t('gReplySub', { promise: niche.promise }),
      give: t(sale ? 'gGiveSale' : 'gGiveSub', { promise: niche.promise }),
      pub: t('gPub', { gender: gender ?? 'other' }),
      post: t('gPost', { word: niche.word, promise: niche.promise }),
      msg1: sale ? t('gMsg1') : undefined,
      msg2: sale ? t('gMsg2') : undefined,
      msg3: sale ? t('gMsg3') : undefined,
    });
  }
  // Письма цепочки нужны только сцене s5; в остальные они бы просто ехали лишними
  // параметрами адреса (в приложении пример открывается ссылкой, и длина её не резиновая).
  const chain = scene == null || scene === 's5';
  return nicheSceneTexts(niche, {
    // §10.12 п.4: в Телеграме (s4) просят подписаться на КАНАЛ, не на аккаунт.
    reply: t(scene === 's4' ? 'replyTg' : 'reply', { promise: niche.promise }),
    give: t('give', { promise: niche.promise }),
    // Ответ под комментарием — всегда «Отправили» (бот пишет от имени аккаунта; решение ведущего 26.10.2026, ТЗ
    // NATIVE-SCENES §17): в словаре формы по полу сняты, `gender` оставлен — строке он не мешает.
    pub: t('pub', { gender: gender ?? 'other' }),
    post: t('post', { word: niche.word, promise: niche.promise }),
    about: t('about', { promise: niche.promise }),
    // §11.20 п.1: письма подарка (`msg1` словаря) в цепочке s5 больше нет — места сцены m1..m3 берут письма 2..4.
    msg1: chain ? t('msg2') : undefined,
    msg2: chain ? t('msg3') : undefined,
    msg3: chain ? t('msg4') : undefined,
  });
}

/**
 * КОПИЯ СЦЕНЫ ПОД НИШУ: слова ниши (nichePhrases) и ФОТО ПОСТА той же ниши (scene-assets
 * `nichePostImg`, владелец 23.10.2026). Своя публикация человека (profile.postImg) главнее фото
 * ниши — personalizeScene ставит её поверх.
 */
export function nicheScene(
  def: SceneDef,
  texts: SceneTexts | null,
  nicheKey: string | null | undefined,
  profile: PersonalProfile | null | undefined,
  locale: string = 'ru',
): SceneDef {
  const out = personalizeScene(def, texts, profile);
  if (!nicheKey || !isWizardScene(def.k)) return out;
  const skin: Partial<SceneDef> = {};
  if (!profile?.postImg) skin.postImg = nichePostImg(nicheKey);
  // QUICK-START §11.34 п.4: у «Продажи» (g2) картинка поста всегда другая, чем у «Подписки» той же пары: своя публикация
  // человека — только в g1 (пост с кодовым словом), у g2 — второе фото ниши.
  if (def.k === 'g2') skin.postImg = nicheSalePostImg(nicheKey);
  // Автор поста — по нише (ник и подпись в шапке), если у человека нет своих (личный портрет главнее).
  const person = nichePerson(nicheKey, locale);
  if (person && !profile?.nick && !profile?.name && def.nick) {
    skin.nick = person.nick;
    if (def.name) skin.name = person.name;
  }
  return { ...out, ...skin };
}

/**
 * КОПИЯ СЦЕНЫ ПОД ЧЕЛОВЕКА. Слова — через общий механизм слотов (applySceneTexts),
 * лицо и картинка — новыми слотами дефа. Ничего не задано — возвращается та же сцена.
 */
export function personalizeScene(
  def: SceneDef,
  texts: SceneTexts | null,
  profile: PersonalProfile | null | undefined,
): SceneDef {
  // Ключ сцены и есть её имя в раскладке слотов (`SceneDef.k` = `demoScene` мастеров).
  // Раскладки нет — слова не подменяем вовсе (новая сцена не должна ронять экран).
  let out = texts && isWizardScene(def.k) ? applySceneTexts(def, def.k, texts) : def;
  // ПОЛ ВЛАДЕЛЬЦА АККАУНТА (О5.4): в примере отправитель — САМ КЛИЕНТ, поэтому реплики
  // от его лица согласуются с его полом. Раскрытие идёт ВСЕГДА, даже когда лица нет:
  // так сырая метка `{{g:…}}` не попадёт на экран ни при каком заходе (пола не знаем —
  // нейтральная форма). Меток в сцене нет — возвращается тот же объект.
  // Описание бота на заставке (s4) — не шаг сцены, а поле дефа: подменяем отдельно.
  // §10.5: у заставки без карточки описания (g2, s5 — `tgAbout.p` пуст) описания нет и после подстановки.
  if (texts?.about && out.tgAbout?.p) out = { ...out, tgAbout: { ...out.tgAbout, p: texts.about } };
  out = expandGenderDeep(out, profile?.gender ?? null);
  if (!profile) return out;

  const faces = (profile.faces ?? [])
    .map((f) => ({ name: f.name ?? null, avatar: f.avatar ?? null }))
    .slice(0, 4);
  const skin: Partial<SceneDef> = {};
  // В примере отправитель — САМ КЛИЕНТ (владелец 31.08: «его аватарка и имя как
  // отправитель»), поэтому имя и ник в шапке диалога подменяются во всех сценах.
  // Имени нет — в шапку идёт ник: иначе у телеграм-сцены остаётся имя бота из сцены
  // («Beyond English») рядом с настоящим лицом клиента.
  const shownName = profile.name || profile.nick || null;
  if (shownName) skin.name = shownName;
  // НИК ВСЕГДА ЧЕЛОВЕКА (найдено 26.10.2026): у доступа бывает имя без ника («@anna.speaks» в имени) — тогда в сцене
  // оставался НАШ ник «scripteraai» под именем клиента. Ника нет — ник из имени (без «@»).
  const ownNick = profile.nick || (shownName ? shownName.replace(/^@/, '') : null);
  if (ownNick) skin.nick = ownNick;
  // ТЕЛЕГРАМ-ЧАСТЬ СЦЕНЫ (правило лица §10.2 п.2): сцена Телеграма целиком или переход в Телеграм из директа (g2) —
  // аватарка бота, нет — аватарка Инстаграма, нет обеих — кружок; имя в шапке Телеграма — имя бота. Прежде у сцены с
  // переходом Телеграм-часть оставалась стоковой (лицо Дарьи под именем человека).
  const hasTg = def.chat === 'tg' || def.steps.some((st) => st.k === 'jump' && st.to === 'tg');
  const tgAva = profile.tg ? profile.tg.avatar || profile.avatar || '' : profile.avatar || '';
  const tgName = profile.tg?.name || null;
  if (hasTg) skin.tgAva = tgAva;
  if (hasTg && tgName) {
    if (def.chat === 'tg') skin.name = tgName;
    else skin.tgName = tgName;
  }
  if (profile.avatar) {
    skin.selfAva = profile.avatar;
  } else {
    // АВАТАРКИ НЕТ (Д5, владелец 03.09.2026: «никогда — чужое лицо под именем клиента»):
    // в шапке стоит имя человека, и фото Дарьи рядом с ним — враньё. Пустая строка —
    // это НЕ «нет значения»: `Pava` рисует на её месте фирменный кружок, а `??` в сцене
    // пустую строку не подменяет. Гость (profile == null) сюда не доходит — у него сцена
    // остаётся ровно такой, какой была.
    skin.selfAva = '';
  }
  if (profile.postImg) skin.postImg = profile.postImg;
  if (faces.length > 0) skin.selfFaces = faces;
  if (Object.keys(skin).length === 0) return out;
  out = { ...out, ...skin };
  return out;
}

// ── ЛИЦО КЛИЕНТА ЧЕРЕЗ АДРЕС СЦЕНЫ (владелец 03.09.2026) ──
// Пример в приложении открывается в изолированном веб-вью БЕЗ куки сессии
// (WizardScreen.swift demoWeb = nonPersistent), поэтому сервер страницы /wizard-scene
// не может узнать человека по входу. Лицо и пол едут ПАРАМЕТРАМИ адреса — так же, как
// свои фразы бота (sceneTextsToQuery). Слова ниши идут отдельно, в t.*-слотах.
const PERSONA_TEXT_MAX = 200;
const persClean = (v: string | null | undefined): string =>
  typeof v === 'string' ? v.trim().slice(0, PERSONA_TEXT_MAX) : '';

/** /api/cabinet/accounts/<cred>/avatar → публичный /api/scene-avatar/<cred> (входа не
 *  требует). Чужой или уже публичный адрес — как есть. */
export function sceneAvatarUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  const m = /^\/api\/cabinet\/accounts\/([^/]+)\/avatar$/.exec(url);
  return m ? `/api/scene-avatar/${m[1]}` : url;
}

/** Лицо клиента → параметры адреса (p.name/p.nick/p.ava/p.img/p.g/p.face). Пусто — нет. */
export function personaToQuery(profile: PersonalProfile | null | undefined): URLSearchParams {
  const out = new URLSearchParams();
  if (!profile) return out;
  const name = persClean(profile.name);
  const nick = persClean(profile.nick);
  // Аватарка едет ПУБЛИЧНЫМ адресом: пример открыт в веб-вью без куки входа
  // (WizardScreen.swift), закрытая /api/cabinet/.../avatar там отдала бы 401. Тот же
  // credId, публичная ручка /api/scene-avatar/<cred> (владелец 03.09.2026).
  const ava = persClean(sceneAvatarUrl(profile.avatar));
  const img = persClean(profile.postImg);
  if (name) out.set('p.name', name);
  if (nick) out.set('p.nick', nick);
  if (ava) out.set('p.ava', ava);
  if (img) out.set('p.img', img);
  if (profile.gender === 'female' || profile.gender === 'male') out.set('p.g', profile.gender);
  // §10.2 п.2: Телеграм-бот человека (имя и аватарка по правилу лица) — тем же адресом; аватарка публичная.
  const tgName = persClean(profile.tg?.name);
  const tgAva = persClean(sceneAvatarUrl(profile.tg?.avatar));
  if (tgName) out.set('p.tgn', tgName);
  if (tgAva) out.set('p.tgava', tgAva);
  // Имя и аватарка лица едут ПАРАМИ (p.face + p.faceav), выровнены по позиции: фото
  // тоже публичным адресом (`/api/scene-face/...`), фото нет — пустая строка (кружок).
  for (const f of (profile.faces ?? []).slice(0, 4)) {
    const nm = persClean(f.name);
    if (!nm) continue;
    out.append('p.face', nm);
    out.append('p.faceav', persClean(f.avatar));
  }
  return out;
}

/** Параметры адреса → лицо клиента. Ни одного p.*-параметра — null (лицо сцены как есть). */
export function personaFromQuery(
  query: Record<string, string | string[] | undefined>,
): PersonalProfile | null {
  const one = (v: string | string[] | undefined): string | null => {
    const s = Array.isArray(v) ? v[0] : v;
    return typeof s === 'string' && s.trim() ? s.trim().slice(0, PERSONA_TEXT_MAX) : null;
  };
  const facesRaw = query['p.face'];
  const avaRaw = query['p.faceav'];
  const avaList = Array.isArray(avaRaw) ? avaRaw : avaRaw ? [avaRaw] : [];
  const faces = (Array.isArray(facesRaw) ? facesRaw : facesRaw ? [facesRaw] : [])
    .map((s, i) => ({
      name: typeof s === 'string' ? s.trim().slice(0, PERSONA_TEXT_MAX) : '',
      // Аватарка лица выровнена по позиции имени (p.faceav того же индекса); пусто — кружок.
      avatar:
        typeof avaList[i] === 'string' && avaList[i]!.trim()
          ? avaList[i]!.trim().slice(0, PERSONA_TEXT_MAX)
          : null,
    }))
    .filter((f) => f.name.length > 0)
    .slice(0, 4);
  const g = one(query['p.g']);
  const profile: PersonalProfile = {
    name: one(query['p.name']),
    nick: one(query['p.nick']),
    avatar: one(query['p.ava']),
    postImg: one(query['p.img']),
    gender: g === 'female' || g === 'male' ? (g as PortraitGender) : null,
    faces,
  };
  const tgName = one(query['p.tgn']);
  const tgAva = one(query['p.tgava']);
  if (tgName || tgAva) profile.tg = { name: tgName, avatar: tgAva };
  const has =
    profile.name || profile.nick || profile.avatar || profile.postImg || profile.gender || faces.length > 0 || profile.tg;
  return has ? profile : null;
}

// ── НИША И ЛИЦО ЧЕЛОВЕКА ЧЕРЕЗ АДРЕС СТАТЬИ СПРАВКИ (ТЗ HELP-WARM-FIX §5, претензия владельца 26.10.2026 «фото не грузится
// в iOS») ──
// Островок статьи в приложении (`?app=1&bare=1`) открыт без куки входа и без кэша кабинета: help-scene не знает нишу
// человека и рисует стоковых персон. Сервер страниц приложения дописывает в адрес статьи и плитки пасхалки слова ниши
// (`n.key`, `n.word`, `n.promise`, `n.gift`, `n.title`) и лицо (`p.*`, как у сцен мастера); help-scene читает их из
// адреса, если они есть. Только добавление: адрес без параметров — как раньше (кэш кабинета или стоковая сцена).
const NICHE_FIELDS = ['key', 'word', 'promise', 'gift', 'title'] as const;

/** Ниша и лицо → параметры адреса статьи. Ничего нет — пусто. */
export function nicheToQuery(niche: NicheView | null | undefined, profile: PersonalProfile | null | undefined): URLSearchParams {
  const out = personaToQuery(profile);
  if (niche?.key) for (const f of NICHE_FIELDS) {
    const v = persClean(niche[f]);
    if (v) out.set(`n.${f}`, v);
  }
  return out;
}

/** Ответ /api/user/niche (как его читает кабинет) → параметры адреса: выбранная ниша (или догадка) и лицо. */
export function nicheQueryOf(data: {
  nicheKey?: string | null;
  suggest?: { nicheKey?: string | null } | null;
  niches?: readonly NicheView[] | null;
  profile?: PersonalProfile | null;
} | null | undefined): string {
  if (!data) return '';
  const key = data.nicheKey ?? data.suggest?.nicheKey ?? null;
  const niche = key ? ((data.niches ?? []).find((n) => n.key === key) ?? null) : null;
  return nicheToQuery(niche, data.profile ?? null).toString();
}

/** Параметры адреса статьи → ниша и лицо; ни одного `n.*`/`p.*` — null. */
export function nicheFromSearch(search: string): { niche: NicheView | null; profile: PersonalProfile | null } | null {
  const sp = new URLSearchParams(search);
  const rec: Record<string, string | string[]> = {};
  for (const k of new Set(sp.keys())) {
    const all = sp.getAll(k);
    rec[k] = all.length > 1 ? all : (all[0] ?? '');
  }
  const profile = personaFromQuery(rec);
  const key = persClean(sp.get('n.key'));
  const niche: NicheView | null = key
    ? {
        key,
        word: persClean(sp.get('n.word')),
        promise: persClean(sp.get('n.promise')),
        gift: persClean(sp.get('n.gift')),
        title: persClean(sp.get('n.title')),
      }
    : null;
  return niche || profile ? { niche, profile } : null;
}
